import React, { useEffect, useState, useReducer } from "react";
import { useSelector } from "react-redux";
import {
    Route,
    Switch,
    useHistory,
    useRouteMatch,
} from "react-router-dom";
import { IReduxAppState } from "../../../reducers";
import { ICallAPIFunction } from "../../../yoello-lib/modules/API2/types";
import { CategoriesDialogForm } from "../../modal/CategoriesDialogForm";
import { DialogRoute } from "../../dialogRoutes/DialogRoute";
import ICategory from "../categories/ICategory";
import ISubCategory from "../categories/ISubCategory";
import { CategoriesListController } from "./categories/CategoriesListController";
import { CategoriesProductListView } from "./CategoriesProductListView";
import { setSubcategoryFilterState } from "./state/CategoriesContainer.actions";
import { EnabledDisabledStatus, ProductCategoryType, subcategoryListInitalState, subcategoryListReducer } from "./state/CategoriesContainer.reducer";
import useParseTabItems from './useParseTabItems';
import { BY_ID, CREATE_URL, PRODUCTS_SUBCATEGORIES } from "../../../const/URL.const";
import { useTranslation } from "react-i18next";
import { useSideTabs } from "../../../hooks/useSideTabs";
import { ISidebarItem } from "../../Routes";


export type subcategoryItemAPICall = ICallAPIFunction<ISubCategory[] | ISubCategory>;
interface ICategoriesContainerProps {
    venueId: string;
    categories: ICategory[];
    subcategories: ISubCategory[];
    isLoading: boolean;
    callSubcategory: subcategoryItemAPICall;
    updateSubcategoryPosition: (positions: string[]) => void;
    tab: ISidebarItem;
}

export function CategoriesContainer({
    venueId,
    categories,
    subcategories,
    isLoading,
    callSubcategory,
    updateSubcategoryPosition,
    tab
}: ICategoriesContainerProps) {
    const [categoryID, setCategoryID] = useState('');
    const { parseCategoryItems } = useParseTabItems();
    const [subcategoryList, setSubCategoriesList] = useState<ISubCategory[]>([]);
    let { path } = useRouteMatch();
    const history = useHistory();
    const { t } = useTranslation();
    const venueData = useSelector((state: IReduxAppState) => state.venue[venueId]?.venueData);
    const isSquare = venueData?.payment_processor === 'SQUARE';
    const { defaultCategoryTab, categoriesLoaded } = parseCategoryItems(categories, subcategories);
    const Venue = useSelector(
        (state: IReduxAppState) => state.venue[venueId]?.venueSettings
    );
    useSideTabs(tab);


    const [state, dispatch] = useReducer(
        subcategoryListReducer,
        subcategoryListInitalState
    );


    useEffect(() => {
        if (categories?.length && !Venue?.on_menu_setup) {
            setCategoryID(defaultCategoryTab);
        }
        //eslint-disable-next-line
    }, [categories, defaultCategoryTab]);

    useEffect(() => {
        let filteredItems: ISubCategory[] = [];
        // if they haven't setup their Menu: Show in old way with Food/Drink tabs
        if (!Venue?.on_menu_setup) {
            if (categories && subcategories && categoryID) {
                filteredItems = subcategories.filter((item) => item.category_id === categoryID)
                    .sort((a, b) => a.sort_position - b.sort_position);
            }
            setSubCategoriesList(filteredItems);

        } else {
            // if they haven setup their Menu: Show all the subcats together in a list
            if (categories && subcategories) {
                filteredItems = subcategories.sort((a, b) => a.sort_position - b.sort_position);
            }

            const FOODID = categories.find(item => item.category_name === "FOOD")?.id
            const DRINKID = categories.find(item => item.category_name === "DRINK")?.id

            const showFood = state.filterState.MainCategoryFilter.includes(ProductCategoryType.FOOD)
            const showDrink = state.filterState.MainCategoryFilter.includes(ProductCategoryType.DRINK)
            if ((showFood && !showDrink) || (showDrink && !showFood)) {
                filteredItems = filteredItems.filter(item => showFood ? item.category_id === FOODID : item.category_id === DRINKID)
                console.log(filteredItems);

            }

            const showDisabled = state.filterState.SubcategoryStatusEnabled.includes(EnabledDisabledStatus.DISABLED)
            const showEnabled = state.filterState.SubcategoryStatusEnabled.includes(EnabledDisabledStatus.ENABLED)

            if ((showDisabled && !showEnabled) || (showEnabled && !showDisabled)) {
                filteredItems = filteredItems.filter(item => showDisabled ? !item.enabled : item.enabled)
                console.log(filteredItems);

            }
            setSubCategoriesList(filteredItems);

        }
        // eslint-disable-next-line
    }, [categories, subcategories, categoryID, state.filterState.MainCategoryFilter, state.filterState.SubcategoryStatusEnabled]);
    function onItemSelection(data: ISubCategory | null) {
        const startURL = PRODUCTS_SUBCATEGORIES;
        if (data) {
            history.push(`${startURL}/${data.id}`, {
                categories,
                formData: data
            })
        } else {
            history.push(`${startURL}/${CREATE_URL}`, {
                categories,
                formData: { enabled: true }
            })
        }
    }

    function setFilters(filterSubmitData: any) {
        // @ts-ignore
        dispatch(setSubcategoryFilterState(filterSubmitData[0].values, filterSubmitData[1].values))
    }

    function getFilters() {
        return [
            {
                key: "MainCategoryFilter",
                label: t('menu.subcategories.mainCat'),
                filters: [
                    {
                        key: ProductCategoryType.FOOD,
                        label: t(`menu.subcategories.${ProductCategoryType.FOOD}`),
                        checked: state.filterState.MainCategoryFilter.includes(ProductCategoryType.FOOD),
                    },
                    {
                        key: ProductCategoryType.DRINK,
                        label: t(`menu.subcategories.${ProductCategoryType.DRINK}`),
                        checked: state.filterState.MainCategoryFilter.includes(ProductCategoryType.DRINK),
                    },
                ]
            }, {
                key: "SubcategoryStatusEnabled",
                label: t('menu.subcategories.subCatStatus'),
                filters: [
                    {
                        key: "ENABLED",
                        label: t('menu.products.enabled'),
                        checked: state.filterState.SubcategoryStatusEnabled.includes(EnabledDisabledStatus.ENABLED),
                    },
                    {
                        key: "DISABLED",
                        label: t('menu.products.disabled'),
                        checked: state.filterState.SubcategoryStatusEnabled.includes(EnabledDisabledStatus.DISABLED),
                    },
                ]
            },
        ]
    }
    return <Switch>
        <Route path={path}>
            <CategoriesListController showFilters={Venue?.on_menu_setup && !isSquare} setFilters={setFilters} filtersList={getFilters()} venueId={venueId} isLoading={isLoading} setCategoryId={setCategoryID} categoryID={categoryID}
                subcategories={subcategories} categories={!Venue?.on_menu_setup ? categories : []} onItemSelection={onItemSelection} />
            <CategoriesProductListView updateSubcategoryPosition={updateSubcategoryPosition} venueId={venueId} filteredSubCategories={subcategoryList}
                subcategories={subcategories} isLoading={isLoading && !categoriesLoaded} onItemSelection={onItemSelection} />
            <Route exact path={`${path}${BY_ID}`}>
                <DialogRoute Component={CategoriesDialogForm}
                    navigateBackRoute={path}
                    ComponentPropsFunctions={{
                        setCategoryID: setCategoryID,
                        callSubcategory: callSubcategory
                    }}
                    dialogTitle={t('menu.subcategories.title')} />
            </Route>
        </Route>
    </Switch>
}